import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, Image as ImageIcon, Sliders, Map, CheckCircle2, Star, ShieldCheck, Zap, Download, Layers, Users, Award, ChevronDown, BookOpen, Gift, Sparkles, Sun, Palette, Briefcase, Smartphone, FileText, X, Rocket, MessageSquare, Lightbulb, Target } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { Button } from './components/Button';
import { SocialProofToast } from './components/SocialProofToast';
import { MetaPixel } from './components/MetaPixel';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.5,
  delay
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const tomos = [{
  number: 1,
  title: 'Dominio del Modo Manual',
  description: 'Deja el modo AUTO para siempre. Aprende a controlar exposición, ISO, apertura y velocidad.',
  price: 'ARS $9.990',
  icon: <Sliders className="w-6 h-6" data-id="element-1" />,
  items: ['Triángulo de exposición', 'Modos de medición', 'Balance de blancos', 'Enfoque manual vs automático']
}, {
  number: 2,
  title: 'Fotografía de Aventura y Naturaleza',
  description: 'Guías de campo para las situaciones más desafiantes en tus viajes fotográficos.',
  price: 'ARS $9.990',
  icon: <Sun className="w-6 h-6" data-id="element-2" />,
  items: ['Auroras boreales', 'Vía Láctea y astrofotografía', 'Fauna salvaje', 'Cascadas y paisajes marinos']
}, {
  number: 3,
  title: 'Composición y Técnica Avanzada',
  description: 'Eleva tus imágenes con técnicas de composición profesional y captura avanzada.',
  price: 'ARS $9.990',
  icon: <Layers className="w-6 h-6" data-id="element-3" />,
  items: ['Regla de tercios y proporción', 'HDR y alto rango dinámico', 'Focus stacking', 'Panoramas y timelapses']
}, {
  number: 4,
  title: 'Edición y Postproducción Profesional',
  description: 'Flujos de trabajo de edición paso a paso para transformar tus fotos en obras maestras.',
  price: 'ARS $9.990',
  icon: <Palette className="w-6 h-6" data-id="element-4" />,
  items: ['Flujo de trabajo en Lightroom', 'Retoque de retratos', 'Color grading cinematográfico', 'Blanco y negro artístico']
}, {
  number: 5,
  title: 'Negocio y Marca Personal',
  description: 'Convierte tu pasión en profesión. Todo lo que necesitas para tu negocio fotográfico.',
  price: 'ARS $10.030',
  icon: <Briefcase className="w-6 h-6" data-id="element-5" />,
  items: ['Cómo fijar tus precios', 'Contratos y plantillas legales', 'Creación de portfolio', 'Marketing en redes sociales']
}, {
  number: 6,
  title: 'Fotografía de Retrato y Dirección',
  description: 'Aprende a crear retratos impactantes que transmitan emociones. Domina la iluminación y las poses.',
  price: 'ARS $12.900',
  icon: <Users className="w-6 h-6" data-id="element-6" />,
  items: ['Poses para hombres, mujeres y parejas', 'Cómo dirigir personas sin experiencia', 'Retratos con luz natural y artificial', 'Expresiones y lenguaje corporal']
}];
const bonos = [{
  title: '30 Presets Profesionales de Lightroom',
  description: 'Presets listos para usar que transformarán tus fotos con un solo clic. Incluye estilos cinematográficos, paisaje y retrato.',
  value: 'ARS $14.900',
  icon: <Sparkles className="w-6 h-6" data-id="element-7" />
}, {
  title: 'Kit de Planificación de Sesiones',
  description: '25 plantillas editables para planificar cada sesión: checklists, contratos, mood boards y cronogramas.',
  value: 'ARS $11.900',
  icon: <FileText className="w-6 h-6" data-id="element-8" />
}, {
  title: 'Guía Maestra de Fotografía con Smartphone',
  description: 'Domina la cámara de tu móvil. Trucos, apps recomendadas y técnicas de composición.',
  value: 'ARS $9.900',
  icon: <Smartphone className="w-6 h-6" data-id="element-9" />
}, {
  title: '100 Poses Profesionales para Retratos',
  description: 'Una colección visual lista para usar en sesiones reales. Nunca más te quedarás sin ideas al fotografiar clientes.',
  value: 'ARS $9.900',
  icon: <Users className="w-6 h-6" data-id="element-10" />
}, {
  title: 'Guía Express de Iluminación Profesional',
  description: 'Descubre cómo lograr fotografías impactantes utilizando cualquier fuente de luz, incluso sin estudio.',
  value: 'ARS $11.900',
  icon: <Sun className="w-6 h-6" data-id="element-11" />
}, {
  title: 'Pack 50 Contratos y Documentos',
  description: 'Plantillas listas para editar y utilizar con clientes reales, ahorrando tiempo y transmitiendo profesionalismo.',
  value: 'ARS $14.900',
  icon: <Briefcase className="w-6 h-6" data-id="element-12" />
}];
const reviews = [{
  name: 'Martín G. — Buenos Aires',
  text: 'Increíble kit. Mejoré mis fotos desde la primera semana. Todo está súper bien organizado.'
}, {
  name: 'Lucía R. — Córdoba',
  text: 'Los presets son espectaculares y las guías muy claras. Lo recomiendo 100%.'
}, {
  name: 'Santiago M. — Mendoza',
  text: 'Llevaba meses buscando algo así. El tomo de negocio me ayudó a conseguir mis primeros clientes.'
}];
export function App() {
  const [selectedPlan, setSelectedPlan] = useState<'basico' | 'premium'>('premium');
  const checkoutUrl = 'https://checkout.tusbeneficios.shop/cart/50929956028649:1';
  const checkoutUrlPremium = 'https://checkout.tusbeneficios.shop/cart/51279237808361:1';
  const productImage = 'https://cdn.magicpatterns.com/uploads/mrofacSwdKLnsrHAkwwEA2/Untitled_design_(2).png';
  return <div className="min-h-screen bg-zinc-950 text-zinc-300 font-sans overflow-x-hidden" data-id="element-13">
      <MetaPixel data-id="element-14" />

      {/* Top Urgency Bar - Ultra Compact */}
      <div className="bg-amber-500 text-zinc-900 py-0.5 px-2 sticky top-0 z-50 shadow-md" data-id="element-15">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-center" data-id="element-16">
          <p className="font-bold text-[9px] sm:text-[10px] uppercase tracking-wider flex items-center gap-1" data-id="element-17">
            <Zap className="w-2.5 h-2.5 fill-current" data-id="element-18" />
            ¡Oferta relámpago!
          </p>
          <div className="font-mono font-bold bg-zinc-950/10 px-1 py-0.5 rounded text-[9px] sm:text-[10px] flex items-center" data-id="element-19">
            <CountdownTimer initialHours={2} initialMinutes={14} initialSeconds={10} className="scale-[0.45] sm:scale-[0.45] origin-center -my-5" data-id="element-20" />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-8 pb-10 px-4 overflow-hidden" data-id="element-21">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-950/30 via-zinc-950 to-zinc-950 -z-10" data-id="element-22" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl -z-10" data-id="element-23" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-amber-600/5 rounded-full blur-3xl -z-10" data-id="element-24" />

        <div className="max-w-5xl mx-auto text-center" data-id="element-25">
          <FadeIn data-id="element-26">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-semibold text-xs mb-5" data-id="element-27">
              <Star className="w-3.5 h-3.5 fill-current" data-id="element-28" />
              ¡56% de descuento sobre el precio de venta al público!
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-29">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-5 leading-tight uppercase" data-id="element-30">
              TU AÑO FOTOGRÁFICO PROFESIONAL{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-600 text-glow" data-id="element-31">
                RESUELTO
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-32">
            <motion.div className="max-w-xs sm:max-w-sm mx-auto mb-6" animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            ease: 'easeInOut',
            repeat: Infinity
          }} whileHover={{
            scale: 1.03
          }} data-id="element-33">
              <img src={productImage} alt="Fotógrafo Pro Kit — La solución fotográfica definitiva" className="w-full h-auto drop-shadow-[0_20px_60px_rgba(245,158,11,0.15)]" data-id="element-34" />
            </motion.div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-35">
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-6 leading-relaxed" data-id="element-36">
              <strong className="text-zinc-200" data-id="element-37">Fotógrafo Pro Kit</strong>{' '}
              simplifica tu flujo de trabajo fotográfico de principio a fin. El
              kit definitivo para cualquier fotógrafo.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} className="flex flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1.5 mb-6 text-[11px] sm:text-xs text-zinc-400 font-medium" data-id="element-38">
            <div className="flex items-center gap-1" data-id="element-39">
              <CheckCircle2 className="w-3 h-3 text-amber-500 shrink-0" data-id="element-40" />
              <span data-id="element-41">Miles de kits vendidos</span>
            </div>
            <span className="text-zinc-700" data-id="element-42">·</span>
            <div className="flex items-center gap-1" data-id="element-43">
              <Map className="w-3 h-3 text-amber-500 shrink-0" data-id="element-44" />
              <span data-id="element-45">Usado en +100 países</span>
            </div>
            <span className="text-zinc-700" data-id="element-46">·</span>
            <div className="flex items-center gap-1" data-id="element-47">
              <Award className="w-3 h-3 text-amber-500 shrink-0" data-id="element-48" />
              <span data-id="element-49">Recomendado por profesionales</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-50">
            <Button size="lg" onClick={() => window.open(checkoutUrl, '_blank')} className="mx-auto" data-id="element-51">
              Obtener mi Fotógrafo Pro Kit
            </Button>
            <p className="mt-2.5 text-xs text-zinc-500 flex items-center justify-center gap-1.5" data-id="element-52">
              <ShieldCheck className="w-3.5 h-3.5" data-id="element-53" /> Garantía de devolución de
              90 días
            </p>
          </FadeIn>
        </div>
      </section>

      {/* As Seen In Placeholder */}
      <section className="border-y border-zinc-800/50 bg-zinc-900/40 py-4 px-4" data-id="element-54">
        <div className="max-w-5xl mx-auto" data-id="element-55">
          <p className="text-center text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-3" data-id="element-56">
            Como se ha publicado en
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-12 opacity-40 grayscale" data-id="element-57">
            <h3 className="text-lg font-serif font-bold" data-id="element-58">PhotoMag</h3>
            <h3 className="text-lg font-black tracking-tighter" data-id="element-59">
              LENS<span className="font-light" data-id="element-60">CRAFT</span>
            </h3>
            <h3 className="text-lg font-mono font-bold" data-id="element-61">APERTURE</h3>
            <h3 className="text-lg font-sans font-bold italic" data-id="element-62">ShutterPro</h3>
          </div>
        </div>
      </section>

      {/* Reviews Section (Moved here) */}
      <section className="py-10 px-4 bg-zinc-950" data-id="element-63">
        <div className="max-w-5xl mx-auto" data-id="element-64">
          <FadeIn data-id="element-65">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-white mb-8" data-id="element-66">
              Lo que dicen nuestros alumnos
            </h3>
            <div className="grid md:grid-cols-3 gap-4" data-id="element-67">
              {reviews.map((review, i) => <div key={i} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-5 hover:border-amber-500/30 transition-colors" data-id="element-68">
                  <div className="flex gap-1 mb-3" data-id="element-69">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-70" />)}
                  </div>
                  <p className="text-zinc-300 text-sm italic mb-4 leading-relaxed" data-id="element-71">
                    "{review.text}"
                  </p>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider" data-id="element-72">
                    {review.name}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Problem / Solution -> EXISTE UN CAMINO MÁS SIMPLE */}
      <section className="py-10 px-4 bg-gradient-to-b from-zinc-950 to-zinc-900/50 relative border-t border-zinc-900" data-id="element-73">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-500/5 via-transparent to-transparent -z-10" data-id="element-74" />

        <div className="max-w-2xl mx-auto text-center" data-id="element-75">
          <FadeIn data-id="element-76">
            <h2 className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-600 mb-4 uppercase tracking-tight" data-id="element-77">
              Existe un camino más simple
            </h2>
            <p className="text-base sm:text-lg text-zinc-300 mb-8 leading-relaxed" data-id="element-78">
              Un sistema dividido en pasos simples para que avances sin
              abrumarte y consigas resultados desde el día 1.
            </p>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-79">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 sm:p-8 inline-block text-left" data-id="element-80">
              <ul className="space-y-4" data-id="element-81">
                <li className="flex items-center gap-3" data-id="element-82">
                  <div className="bg-amber-500/10 p-1.5 rounded-full" data-id="element-83">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" data-id="element-84" />
                  </div>
                  <span className="text-white font-medium text-base sm:text-lg" data-id="element-85">
                    Sin lenguaje técnico complicado
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-86">
                  <div className="bg-amber-500/10 p-1.5 rounded-full" data-id="element-87">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" data-id="element-88" />
                  </div>
                  <span className="text-white font-medium text-base sm:text-lg" data-id="element-89">
                    Directo a la práctica
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-90">
                  <div className="bg-amber-500/10 p-1.5 rounded-full" data-id="element-91">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" data-id="element-92" />
                  </div>
                  <span className="text-white font-medium text-base sm:text-lg" data-id="element-93">
                    Con ejemplos reales y visuales
                  </span>
                </li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 px-4 bg-gradient-to-b from-zinc-900/50 to-zinc-900/20 border-y border-zinc-800/50 relative" data-id="element-94">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl -z-10" data-id="element-95" />

        <div className="max-w-5xl mx-auto" data-id="element-96">
          <FadeIn data-id="element-97">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8" data-id="element-98">
              Prepárate para no volver a fallar el tiro
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-4" data-id="element-99">
            {[{
            icon: <Download className="w-6 h-6 text-amber-500" data-id="element-100" />,
            title: 'Llévalo a todas partes',
            desc: 'Son lo suficientemente prácticas para guardarlas en tu móvil o imprimirlas para tu bolsa de cámara.'
          }, {
            icon: <Layers className="w-6 h-6 text-amber-500" data-id="element-101" />,
            title: 'Para todos los niveles',
            desc: 'Tanto si eres un profesional experimentado como si estás empezando, este kit es un recurso valioso.'
          }, {
            icon: <Camera className="w-6 h-6 text-amber-500" data-id="element-102" />,
            title: 'Concéntrate en disparar',
            desc: 'Al tener toda la información clave en un solo lugar, puedes concentrarte en capturar fotos increíbles.'
          }].map((benefit, i) => <FadeIn key={i} delay={i * 0.1} className="bg-zinc-900/50 p-4 rounded-xl border border-zinc-800/50 hover:border-amber-500/30 transition-colors" data-id="element-103">
                <div className="bg-zinc-950 w-10 h-10 rounded-lg flex items-center justify-center mb-3 border border-zinc-800" data-id="element-104">
                  {benefit.icon}
                </div>
                <h3 className="text-base font-bold text-white mb-1.5" data-id="element-105">
                  {benefit.title}
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed" data-id="element-106">
                  {benefit.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-10 px-4" data-id="element-107">
        <div className="max-w-5xl mx-auto" data-id="element-108">
          <FadeIn className="text-center mb-8" data-id="element-109">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-110">
              ¡Todo lo que incluye el Fotógrafo Pro Kit!
            </h2>
            <p className="text-base text-amber-400 font-medium" data-id="element-111">
              6 Tomos completos + 6 Bonos exclusivos = Tu kit fotográfico
              definitivo
            </p>
          </FadeIn>

          {/* Section Label: Tomos & Total Value */}
          <FadeIn className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4 bg-zinc-900/40 p-4 rounded-xl border border-zinc-800/50" data-id="element-112">
            <div data-id="element-113">
              <div className="flex items-center gap-2 mb-1" data-id="element-114">
                <BookOpen className="w-5 h-5 text-amber-500" data-id="element-115" />
                <h3 className="text-xl font-bold text-white" data-id="element-116">Los 6 Tomos</h3>
              </div>
              <p className="text-zinc-400 text-xs" data-id="element-117">
                El corazón del Fotógrafo Pro Kit — conocimiento profesional
                organizado paso a paso.
              </p>
            </div>
            <div className="text-left sm:text-right" data-id="element-118">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-0.5" data-id="element-119">
                Valor total de los 6 Tomos
              </p>
              <p className="text-base font-bold text-zinc-500 line-through decoration-red-500/50" data-id="element-120">
                ARS $62.890
              </p>
              <p className="text-lg font-black text-amber-400 mt-0.5" data-id="element-121">
                ¡APROVECHALO HOY POR SOLO ARS $19.990!
              </p>
            </div>
          </FadeIn>

          {/* Tomos Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-10" data-id="element-122">
            {tomos.map((tomo, i) => <FadeIn key={i} delay={i * 0.05} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-amber-500/30 transition-all group" data-id="element-123">
                <div className="flex items-start justify-between gap-2 mb-3" data-id="element-124">
                  <div className="flex items-center gap-2.5" data-id="element-125">
                    <div className="bg-amber-500/10 text-amber-500 w-9 h-9 rounded-lg flex items-center justify-center border border-amber-500/20 group-hover:bg-amber-500/20 transition-colors shrink-0" data-id="element-126">
                      {tomo.icon}
                    </div>
                    <div data-id="element-127">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-amber-500/70" data-id="element-128">
                        Tomo {tomo.number}
                      </span>
                      <h4 className="text-sm font-bold text-white leading-tight" data-id="element-129">
                        {tomo.title}
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-zinc-400 text-[11px] mb-3 leading-relaxed" data-id="element-130">
                  {tomo.description}
                </p>
                <ul className="space-y-1.5 mb-3" data-id="element-131">
                  {tomo.items.map((item, j) => <li key={j} className="flex items-start gap-1.5 text-[11px]" data-id="element-132">
                      <CheckCircle2 className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" data-id="element-133" />
                      <span className="text-zinc-300" data-id="element-134">{item}</span>
                    </li>)}
                </ul>
                <div className="pt-3 border-t border-zinc-800/50 text-right" data-id="element-135">
                  <span className="text-[10px] text-zinc-500 uppercase font-semibold" data-id="element-136">
                    Valor individual:{' '}
                  </span>
                  <span className="text-xs font-bold text-zinc-300 line-through decoration-red-500/50" data-id="element-137">
                    {tomo.price}
                  </span>
                </div>
              </FadeIn>)}
          </div>

          {/* Section Label: Bonos */}
          <FadeIn className="mb-5" data-id="element-138">
            <div className="flex items-center gap-2 mb-1" data-id="element-139">
              <Gift className="w-5 h-5 text-emerald-400" data-id="element-140" />
              <h3 className="text-xl font-bold text-white" data-id="element-141">
                6 Bonos Exclusivos{' '}
                <span className="text-emerald-400" data-id="element-142">¡GRATIS POR HOY!</span>
              </h3>
            </div>
            <p className="text-zinc-400 text-xs" data-id="element-143">
              Solo por hoy, recibirás estos 6 bonos valorados en más de ARS
              $73.400 totalmente gratis.
            </p>
          </FadeIn>

          {/* Bonos */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-144">
            {bonos.map((bono, i) => <FadeIn key={i} delay={i * 0.08} className="relative bg-gradient-to-b from-emerald-950/30 to-zinc-900 border border-emerald-500/20 rounded-xl p-4 hover:border-emerald-400/40 transition-all group" data-id="element-145">
                <div className="absolute -top-2.5 right-3 bg-emerald-500 text-zinc-950 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-id="element-146">
                  Gratis — Valor {bono.value}
                </div>
                <div className="bg-emerald-500/10 text-emerald-400 w-9 h-9 rounded-lg flex items-center justify-center border border-emerald-500/20 mb-2.5 group-hover:bg-emerald-500/20 transition-colors" data-id="element-147">
                  {bono.icon}
                </div>
                <h4 className="text-sm font-bold text-white mb-1.5" data-id="element-148">
                  {bono.title}
                </h4>
                <p className="text-zinc-400 text-[11px] leading-relaxed" data-id="element-149">
                  {bono.description}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Offer Section (Pricing Boxes) */}
      <section className="py-14 px-4 bg-gradient-to-b from-zinc-900 to-zinc-950 relative overflow-hidden" data-id="element-150">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" data-id="element-151" />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl -z-10" data-id="element-152" />

        <div className="max-w-3xl mx-auto" data-id="element-153">
          <FadeIn className="text-center mb-10" data-id="element-154">
            <div className="mb-4" data-id="element-155">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-2 flex items-center justify-center gap-1.5" data-id="element-156">
                <Zap className="w-3 h-3" data-id="element-157" /> ¡Oferta relámpago: solo quedan unas
                horas!
              </p>
              <CountdownTimer initialHours={2} initialMinutes={14} initialSeconds={10} data-id="element-158" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3 uppercase" data-id="element-159">
              Elegí tu{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-600" data-id="element-160">
                Plan Ideal
              </span>
            </h2>
            <p className="text-zinc-400 text-sm" data-id="element-161">
              Ambas opciones incluyen los 6 tomos y 6 bonos completos
            </p>
          </FadeIn>

          <div className="space-y-4 mb-8" data-id="element-162">
            {/* Plan Básico */}
            <FadeIn delay={0.1} data-id="element-163">
              <div onClick={() => setSelectedPlan('basico')} className={`cursor-pointer rounded-2xl p-5 sm:p-6 border-2 transition-all flex items-center gap-4 sm:gap-6 ${selectedPlan === 'basico' ? 'bg-zinc-900 border-amber-500/50 shadow-[0_0_20px_-5px_rgba(245,158,11,0.2)]' : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'}`} data-id="element-164">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === 'basico' ? 'border-amber-500' : 'border-zinc-600'}`} data-id="element-165">
                  {selectedPlan === 'basico' && <div className="w-3 h-3 bg-amber-500 rounded-full" data-id="element-166" />}
                </div>
                <div className="flex-1" data-id="element-167">
                  <div className="flex items-center gap-2 mb-1" data-id="element-168">
                    <span className="text-lg" data-id="element-169">✌️</span>
                    <h3 className="text-lg font-bold text-white" data-id="element-170">
                      SISTEMA FÁCIL
                    </h3>
                  </div>
                  <p className="text-zinc-400 text-xs mb-2" data-id="element-171">
                    6 tomos + 6 bonos estratégicos
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-172">
                    <span className="text-sm text-zinc-500 line-through decoration-red-500/50 font-bold" data-id="element-173">
                      ARS $62.890
                    </span>
                    <span className="text-2xl font-black text-white" data-id="element-174">
                      ARS $19.990
                    </span>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Plan Profesional */}
            <FadeIn delay={0.2} data-id="element-175">
              <div onClick={() => setSelectedPlan('premium')} className={`cursor-pointer rounded-2xl p-5 sm:p-6 border-2 transition-all flex items-center gap-4 sm:gap-6 relative overflow-hidden ${selectedPlan === 'premium' ? 'bg-gradient-to-r from-amber-950/40 to-zinc-900 border-amber-500 shadow-[0_0_30px_-5px_rgba(245,158,11,0.3)]' : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'}`} data-id="element-176">
                <div className="absolute top-0 right-0 bg-amber-500 text-zinc-950 text-[9px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-lg" data-id="element-177">
                  Recomendado
                </div>

                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === 'premium' ? 'border-amber-500' : 'border-zinc-600'}`} data-id="element-178">
                  {selectedPlan === 'premium' && <div className="w-3 h-3 bg-amber-500 rounded-full" data-id="element-179" />}
                </div>

                <div className="flex-1" data-id="element-180">
                  <div className="flex items-center gap-2 mb-2" data-id="element-181">
                    <Rocket className="w-5 h-5 text-amber-500" data-id="element-182" />
                    <h3 className="text-lg font-bold text-amber-400" data-id="element-183">
                      PLAN PROFESIONAL
                    </h3>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-184">
                    <span className="bg-amber-500 text-zinc-950 text-[10px] font-bold uppercase px-2 py-0.5 rounded flex items-center gap-1" data-id="element-185">
                      <Star className="w-3 h-3 fill-current" data-id="element-186" /> Más elegido
                    </span>
                    <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-emerald-500/20" data-id="element-187">
                      Ahorrás ARS $54.000
                    </span>
                  </div>

                  <p className="text-zinc-300 text-xs mb-3" data-id="element-188">
                    6 tomos + 6 bonos{' '}
                    <strong className="text-white" data-id="element-189">
                      + 3 módulos profesionales (IA, Ideas, Captación)
                    </strong>
                  </p>

                  <div className="flex items-baseline gap-2" data-id="element-190">
                    <span className="text-sm text-zinc-500 line-through decoration-red-500/50 font-bold" data-id="element-191">
                      ARS $81.990
                    </span>
                    <span className="text-3xl font-black text-white" data-id="element-192">
                      ARS $27.990
                    </span>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} data-id="element-193">
            <Button size="xl" fullWidth className="mb-4 shadow-2xl shadow-amber-500/20" onClick={() => window.open(selectedPlan === 'premium' ? checkoutUrlPremium : checkoutUrl, '_blank')} data-id="element-194">
              ¡SÍ, QUIERO EL{' '}
              {selectedPlan === 'premium' ? 'PLAN PROFESIONAL' : 'SISTEMA FÁCIL'}
              !
            </Button>
            <p className="text-center text-zinc-500 text-xs italic mb-6" data-id="element-195">
              La opción más elegida por quienes quieren profesionalizarse de
              verdad
            </p>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs font-medium text-zinc-400" data-id="element-196">
              <span className="flex items-center gap-1.5" data-id="element-197">
                <ShieldCheck className="w-4 h-4" data-id="element-198" /> Pago seguro
              </span>
              <span className="flex items-center gap-1.5" data-id="element-199">
                <Zap className="w-4 h-4" data-id="element-200" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-201">
                <CheckCircle2 className="w-4 h-4" data-id="element-202" /> Garantía 90 días
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Pack Premium Comparison Section */}
      <section className="py-14 px-4 bg-zinc-950 relative border-t border-zinc-900" data-id="element-203">
        <div className="max-w-5xl mx-auto" data-id="element-204">
          <FadeIn className="text-center mb-10" data-id="element-205">
            <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-bold text-xs uppercase tracking-wider mb-4" data-id="element-206">
              <Award className="w-4 h-4" data-id="element-207" />
              Exclusivo Plan Profesional
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-white mb-3" data-id="element-208">
              ¿Qué vas a obtener con el{' '}
              <span className="text-amber-400" data-id="element-209">PLAN PROFESIONAL</span>?
            </h2>
            <p className="text-zinc-400 text-sm sm:text-base" data-id="element-210">
              La diferencia entre aprender fotografía... y convertirla en una
              verdadera fuente de ingresos
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6 mb-16" data-id="element-211">
            {/* Sin Pack */}
            <FadeIn delay={0.1} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 sm:p-8" data-id="element-212">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-zinc-800 text-zinc-400 text-[10px] font-bold uppercase tracking-wider mb-6" data-id="element-213">
                <X className="w-3.5 h-3.5" data-id="element-214" /> Si no tenés el Plan Profesional
              </div>
              <h3 className="text-xl font-bold text-white mb-6" data-id="element-215">
                Solo aprendés a sacar mejores fotos
              </h3>
              <ul className="space-y-4" data-id="element-216">
                {['Dependés de la inspiración para crear contenido', 'Perdés tiempo buscando ideas para sesiones', 'No sabés cómo atraer clientes constantemente', 'No aprovechás las herramientas de IA a tu favor'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-217">
                    <X className="w-5 h-5 text-zinc-600 shrink-0" data-id="element-218" />
                    <span className="text-zinc-400 text-sm" data-id="element-219">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>

            {/* Con Pack */}
            <FadeIn delay={0.2} className="bg-gradient-to-b from-amber-950/40 to-zinc-900 border-2 border-amber-500/30 rounded-2xl p-6 sm:p-8 relative shadow-[0_0_30px_-10px_rgba(245,158,11,0.2)]" data-id="element-220">
              <div className="absolute -top-3.5 right-6 bg-amber-500 text-zinc-950 font-bold px-4 py-1 rounded-full text-[10px] uppercase tracking-wider" data-id="element-221">
                Recomendado
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-amber-500/10 text-amber-400 text-[10px] font-bold uppercase tracking-wider mb-6 border border-amber-500/20" data-id="element-222">
                <CheckCircle2 className="w-3.5 h-3.5" data-id="element-223" /> Si tenés el Plan
                Profesional
              </div>
              <h3 className="text-xl font-bold text-white mb-6" data-id="element-224">
                Tenés un sistema completo para crecer
              </h3>
              <ul className="space-y-4" data-id="element-225">
                {['Herramientas de IA para ahorrar horas de trabajo', 'Ideas listas para vender y generar ingresos', 'Estrategias para conseguir más clientes', 'Sistema completo para profesionalizar tu negocio'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-226">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" data-id="element-227" />
                    <span className="text-zinc-200 text-sm font-medium" data-id="element-228">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>

          <FadeIn className="text-center mb-10" data-id="element-229">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-230">
              3 Herramientas Exclusivas del Plan Profesional
            </h3>
            <p className="text-zinc-400 text-sm" data-id="element-231">
              Solo disponibles si elegís el Plan Profesional
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-5" data-id="element-232">
            {[{
            icon: <MessageSquare className="w-6 h-6 text-amber-500" data-id="element-233" />,
            title: 'Mega Pack de 200 Prompts con IA',
            desc: 'Automatizá tareas, generá contenido y ahorrá horas utilizando prompts diseñados para fotógrafos.',
            items: ['Prompts para ChatGPT', 'Para Instagram y Reels', 'Para conseguir clientes']
          }, {
            icon: <Lightbulb className="w-6 h-6 text-amber-500" data-id="element-234" />,
            title: 'Biblioteca de 150 Ideas Rentables',
            desc: 'Nunca más te quedes sin ideas. Descubrí sesiones altamente demandadas para vender todo el año.',
            items: ['Retratos profesionales', 'Parejas y lifestyle', 'Fotografía corporativa']
          }, {
            icon: <Target className="w-6 h-6 text-amber-500" data-id="element-235" />,
            title: 'Manual de Captación de Clientes',
            desc: 'Un sistema paso a paso para atraer clientes, generar confianza y convertir consultas en ventas.',
            items: ['Estrategias para Instagram', 'Captación por WhatsApp', 'Scripts de venta']
          }].map((tool, i) => <FadeIn key={i} delay={i * 0.1} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-amber-500/30 transition-colors" data-id="element-236">
                <div className="flex items-center gap-3 mb-4" data-id="element-237">
                  <div className="bg-amber-500/10 p-2 rounded-lg border border-amber-500/20" data-id="element-238">
                    {tool.icon}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500" data-id="element-239">
                    Módulo Premium #{i + 1}
                  </span>
                </div>
                <h4 className="text-lg font-bold text-white mb-3 leading-tight" data-id="element-240">
                  {tool.title}
                </h4>
                <p className="text-zinc-400 text-xs leading-relaxed mb-4" data-id="element-241">
                  {tool.desc}
                </p>
                <ul className="space-y-2" data-id="element-242">
                  {tool.items.map((item, j) => <li key={j} className="flex items-center gap-2 text-[11px] text-zinc-300" data-id="element-243">
                      <CheckCircle2 className="w-3 h-3 text-amber-500" data-id="element-244" />
                      {item}
                    </li>)}
                </ul>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.2} className="mt-12 text-center" data-id="element-245">
            <Button size="lg" className="mx-auto" onClick={() => window.open(checkoutUrlPremium, '_blank')} data-id="element-246">
              Quiero el Plan Profesional
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 py-8 border-t border-zinc-900 text-center px-4" data-id="element-247">
        <div className="max-w-4xl mx-auto" data-id="element-248">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs text-zinc-500 mb-6" data-id="element-249">
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-250">
              Política de privacidad
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-251">
              Términos y condiciones
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-252">
              Política de reembolsos
            </a>
            <a href="#" className="hover:text-amber-500 transition-colors" data-id="element-253">
              Contacto
            </a>
          </div>
          <p className="text-zinc-600 text-[10px] mb-2" data-id="element-254">
            © Copyright 2026 por Fotógrafo Pro Kit, Inc.
          </p>
          <p className="text-[9px] text-zinc-700 max-w-2xl mx-auto leading-tight" data-id="element-255">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

      <SocialProofToast data-id="element-256" />
    </div>;
}